import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Input, Button } from '../../components/ui';
import { Lock, Mail, User as UserIcon } from 'lucide-react';
import { registerUser, AuthError } from '../../lib/auth';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/useToast';

interface FormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
}

const RegisterPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useToast();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  React.useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      addToast({
        type: 'error',
        message: t('registerPage.validation.nameRequired'),
      });
      return false;
    }
    if (!formData.email.trim()) {
      addToast({
        type: 'error',
        message: t('registerPage.validation.emailRequired'),
      });
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      addToast({
        type: 'error',
        message: t('registerPage.validation.emailInvalid'),
      });
      return false;
    }
    if (!formData.password) {
      addToast({
        type: 'error',
        message: t('registerPage.validation.passwordRequired'),
      });
      return false;
    }
    if (formData.password.length < 6) {
      addToast({
        type: 'error',
        message: t('registerPage.validation.passwordTooShort'),
      });
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      addToast({
        type: 'error',
        message: t('registerPage.validation.passwordsDoNotMatch'),
      });
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      await registerUser(
        formData.email,
        formData.password
      );

      addToast({
        type: 'success',
        message: t('registerPage.success.registrationComplete'),
      });

      navigate('/login');
    } catch (error: any) {
      const authError = error as AuthError;
      addToast({
        type: 'error',
        message: authError.message || t('auth.errors.unknown'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      handleSubmit();
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <img
            src="/praxi-logo.png"
            alt="Praxi"
            className="h-12 mx-auto mb-6"
          />
          <h1 className="text-3xl font-bold text-gray-900">
            {t('registerPage.title')}
          </h1>
          <p className="mt-2 text-gray-600">
            {t('registerPage.subtitle')}
          </p>
        </div>

        <div className="bg-white p-8 rounded-xl shadow-md border border-gray-200">
          <div className="space-y-6">
            <Input
              label={t('registerPage.form.nameLabel')}
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              leftIcon={<UserIcon className="w-5 h-5 text-gray-400" />}
              placeholder={t('registerPage.form.namePlaceholder')}
              required
              disabled={isLoading}
              onKeyPress={handleKeyPress}
              autoComplete="name"
            />

            <Input
              label={t('registerPage.form.emailLabel')}
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              leftIcon={<Mail className="w-5 h-5 text-gray-400" />}
              placeholder={t('registerPage.form.emailPlaceholder')}
              required
              disabled={isLoading}
              onKeyPress={handleKeyPress}
              autoComplete="email"
            />

            <Input
              label={t('registerPage.form.passwordLabel')}
              type="password"
              value={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              leftIcon={<Lock className="w-5 h-5 text-gray-400" />}
              placeholder={t('registerPage.form.passwordPlaceholder')}
              required
              disabled={isLoading}
              onKeyPress={handleKeyPress}
              autoComplete="new-password"
            />

            <Input
              label={t('registerPage.form.confirmPasswordLabel')}
              type="password"
              value={formData.confirmPassword}
              onChange={(e) =>
                handleInputChange('confirmPassword', e.target.value)
              }
              leftIcon={<Lock className="w-5 h-5 text-gray-400" />}
              placeholder={t('registerPage.form.confirmPasswordPlaceholder')}
              required
              disabled={isLoading}
              onKeyPress={handleKeyPress}
              autoComplete="new-password"
            />

            <Button
              variant="primary"
              className="w-full"
              onClick={handleSubmit}
              isLoading={isLoading}
              disabled={isLoading}
            >
              {t('registerPage.form.submitButton')}
            </Button>
          </div>
          
          <p className="mt-8 text-center text-sm text-gray-600">
            {t('registerPage.signInPrompt')}{' '}
            <Link
              to="/login"
              className="font-medium text-indigo-600 hover:text-indigo-500"
            >
              {t('registerPage.signInLink')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;